import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ArrowRight, Check, AlertTriangle } from "lucide-react";
import useTitanApp from "../../hooks/useTitanApp";
import { userChangeCompany } from "@/dispatchers/appDispatchers";
import { deleteInvite } from "@/dispatchers/memberDispatchers";

export default function Login({ signout }) {
  const { onboardingVerificationState, appState } = useTitanApp();
  const { currentUser } = appState || {};
  const {
    inviteMemberLogsData = [],
    isInviteMemberLogsLoading,
  } = onboardingVerificationState || {};
  const navigate = useNavigate();
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [confirmTransfer, setConfirmTransfer] = useState(false);

  // ✅ Unique companies
  const companies = useMemo(() => {
    return [
      ...new Map(
        inviteMemberLogsData?.map((item) => [item.companyId, item]),
      ).values(),
    ];
  }, [inviteMemberLogsData]);

  // ✅ Add type (current / new)
  const mappedCompanies = useMemo(() => {
    return companies?.map((company) => ({
      ...company,
      type: company.companyId === currentUser?.companyID ? "current" : "new",
    }));
  }, [companies, currentUser?.companyID]);

  const selectedCompanyData = mappedCompanies.find(
    (c) => c.companyId === selectedCompany,
  );

  const currentCompany = mappedCompanies.find(
    (company) => company.type === "current",
  );

  const isNewSelected = selectedCompanyData?.type === "new";

  const switchWarningText = isNewSelected
    ? `By continuing, you’ll be removed from ${currentCompany?.companyName} and linked to ${selectedCompanyData?.companyName}.`
    : "";

  const completeLogin = async () => {
    try {
      const actionType = selectedCompanyData?.type === "current" ? 1 : 0;
      const inviteId = selectedCompanyData?._id;

      if (actionType === 0) {
        await userChangeCompany({
          email: currentUser?.emailAddress,
          type: actionType,
          companyId: selectedCompany,
        });
        await deleteInvite(inviteId);
      }

      navigate(`/`);
    } catch (error) {
      console.error(error);
    }
  };

  const handleContinueTransfer = () => {
    localStorage.setItem("companySelectionRedirected", "false");
    if (!selectedCompany) return;
    completeLogin();
  };

  const handleBackToLogin = () => {
    signout();
  };

  return (
    <div className="min-h-screen w-full bg-black flex items-center justify-center p-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl p-8">
        <div className="flex items-center justify-center mb-6">
          <img
            src="https://cdn.builder.io/api/v1/image/assets%2F0407d02f319145f582bec36d8e4031e2%2F593f4e759f92476fb077f3a40a77bbc3?format=webp&width=800"
            alt="TITANVERSE"
            className="h-8 w-auto"
          />
        </div>

        <div className="text-center mb-1">
          <h1 className="text-xl font-semibold text-gray-900">
            Choose a company to sign in to
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            You can only be linked to one company at a time. Pick where you want
            to work today.
          </p>
        </div>

        <div className="space-y-3 mt-6 mb-5">
          {isInviteMemberLogsLoading
            ? // Skeleton loaders
              Array.from({ length: 2 }).map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="w-full rounded-xl border border-slate-200 bg-white p-4 animate-pulse"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:gap-5 gap-4">
                    <div className="h-14 w-14 rounded-2xl bg-slate-200" />
                    <div className="flex-1 space-y-3">
                      <div className="h-4 bg-slate-200 rounded w-40" />
                      <div className="h-3 bg-slate-200 rounded w-60" />
                      <div className="h-3 bg-slate-200 rounded w-48" />
                    </div>
                    <div className="h-5 w-5 rounded-full bg-slate-200" />
                  </div>
                </div>
              ))
            : mappedCompanies?.map((company) => {
                const isSelected = selectedCompany === company.companyId;
                const isCurrent = company.type === "current";
                const companyInfoText = isCurrent
                  ? `Stay signed in to your existing company. No changes.`
                  : `Switch here and you'll be removed from ${currentCompany?.companyName}.`;

                return (
                  <button
                    key={company.companyId}
                    type="button"
                    onClick={() => setSelectedCompany(company.companyId)}
                    className={`w-full text-left rounded-xl border p-4 transition-all duration-200 flex flex-col sm:flex-row sm:items-center sm:gap-5 gap-4 ${
                      isSelected
                        ? "border-fuchsia-500 bg-fuchsia-50/70 shadow-[0_20px_45px_rgba(232,121,249,0.16)]"
                        : "border-slate-200 bg-white hover:border-slate-300"
                    }`}
                  >
                    <div
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl text-xl font-semibold ${
                        isCurrent
                          ? "bg-slate-200 text-slate-700"
                          : "bg-fuchsia-500 text-white"
                      }`}
                    >
                      {company.companyName?.charAt(0) || "C"}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-gray-900 text-sm truncate">
                          {company.companyName}
                        </p>
                        <span
                          className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                            isCurrent
                              ? "border-slate-200 bg-slate-100 text-slate-700"
                              : "border-fuchsia-200 bg-fuchsia-100 text-fuchsia-700"
                          }`}
                        >
                          {isCurrent ? "Current" : "New invite"}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                        <Building2 className="h-3 w-3" />
                        {company.address || "No address available"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {companyInfoText}
                      </p>
                    </div>
                    <span
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? "border-fuchsia-500 bg-fuchsia-500"
                          : "border-slate-300 bg-white"
                      }`}
                    >
                      {isSelected && <Check className="h-3 w-3 text-white" />}
                    </span>
                  </button>
                );
              })}
        </div>

        {isNewSelected && !isInviteMemberLogsLoading && (
          <div className="p-3 mb-4 rounded-md bg-amber-50 border border-amber-200 space-y-2">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-900">{switchWarningText}</p>
            </div>
            <label className="flex items-start gap-2 cursor-pointer select-none pl-6">
              <input
                type="checkbox"
                checked={confirmTransfer}
                onChange={(e) => setConfirmTransfer(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-fuchsia-500"
              />
              <span className="text-xs text-amber-900">
                I understand this change and confirm I want to switch companies.
              </span>
            </label>
          </div>
        )}

        <button
          type="button"
          onClick={handleContinueTransfer}
          disabled={
            !selectedCompany ||
            (isNewSelected && !confirmTransfer) ||
            isInviteMemberLogsLoading
          }
          className="w-full py-3 rounded-md bg-fuchsia-500 hover:bg-fuchsia-600 disabled:bg-fuchsia-200 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={handleBackToLogin}
          disabled={isInviteMemberLogsLoading}
          className="w-full mt-2 py-2 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}
